import { useEffect, useState } from 'react';
import type { NavigationItemDto } from '@ecom/contracts';
import { useNavigationStore } from '@/stores/navigation.store';
import { settingValue, useSettingsStore } from '@/stores/settings.store';
import { AsyncSelect } from '@/components/async-select';
import { SortableGrid, SortableItem } from '@/components/sortable';
import { contentPageChoices, type PageChoice } from '@/lib/menu-targets';
import { categorySlugOptions } from '@/lib/options';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import type { MessageKey } from '@/i18n/messages';
import { GripIcon, PlusIcon, TrashIcon } from '@/components/icons';
import { Page, SaveBar } from '@/components/page';
import { Alert, Button, Card, EmptyState, Field, Input, Select, Skeleton } from '@/components/ui';

const KINDS = ['products', 'category', 'page', 'link'] as const;

/**
 * The header menu.
 *
 * Navigation used to be the category tree and nothing else, so a store could
 * not add "Offers" or an about page to its own header — the first thing every
 * store asks for.
 *
 * **An empty menu is not an empty header.** Save nothing and the storefront
 * keeps the category tree it always had, which is said on the screen so nobody
 * clears the menu expecting a blank bar.
 *
 * The menu is saved whole, so it saves from one bar pinned to the bottom of the
 * window rather than a button that scrolls away with the first item.
 *
 * A menu that could not be read is not shown as an empty one: saving over it
 * would replace the store's real menu with whatever was typed here.
 */
export function NavigationPage() {
  const { items: stored, loading, saving, error, load, save } = useNavigationStore();
  const { entries, load: loadSettings } = useSettingsStore();
  const t = useT();

  // The edited copy; null while it is the menu as saved.
  const [draft, setDraft] = useState<NavigationItemDto[] | null>(null);
  const items = draft ?? stored ?? [];
  const dirty = draft !== null;

  useEffect(() => {
    void load();
  }, [load]);

  // The pages the shop has (`pages.content`), so a page item offers what exists.
  useEffect(() => {
    void loadSettings();
  }, [loadSettings]);

  const inUse = items
    .flatMap((item) => [item, ...item.children])
    .filter((item) => item.kind === 'page')
    .map((item) => item.value);
  const pages = contentPageChoices(settingValue(entries, 'pages.content'), inUse);

  const change = (next: NavigationItemDto[]) => setDraft(next);

  const addItem = (parentId?: string) => {
    const fresh: NavigationItemDto = {
      id: `new-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      kind: 'category',
      label: { ar: '', en: '' },
      value: '',
      children: [],
    };

    change(
      parentId
        ? items.map((item) =>
            item.id === parentId ? { ...item, children: [...item.children, fresh] } : item,
          )
        : [...items, fresh],
    );
  };

  const editItem = (id: string, patch: Partial<NavigationItemDto>) => {
    const apply = (list: NavigationItemDto[]): NavigationItemDto[] =>
      list.map((item) =>
        item.id === id ? { ...item, ...patch } : { ...item, children: apply(item.children) },
      );

    change(apply(items));
  };

  const removeItem = (id: string) => {
    const drop = (list: NavigationItemDto[]): NavigationItemDto[] =>
      list.filter((item) => item.id !== id).map((item) => ({ ...item, children: drop(item.children) }));

    change(drop(items));
  };

  return (
    <Page
      title={t('navigationMenu')}
      subtitle={t('navigationSubtitle')}
      width="wide"
      actions={
        <Button variant="secondary" onClick={() => addItem()} disabled={stored === null}>
          <PlusIcon className="size-4" />
          {t('addMenuItem')}
        </Button>
      }
    >
      {stored === null ? (
        loading || !error ? (
          <Skeleton className="h-64" />
        ) : (
          <Card className="flex flex-1 flex-col">
            <EmptyState
              title={error}
              action={
                <Button variant="secondary" onClick={() => void load()}>
                  {t('tryAgain')}
                </Button>
              }
            />
          </Card>
        )
      ) : items.length === 0 ? (
        <Card className="flex flex-1 flex-col">
          <EmptyState title={t('menuEmpty')} body={t('menuEmptyHint')} />
        </Card>
      ) : (
        <SortableGrid items={items} className="flex flex-col gap-3" onReorder={change}>
          {(item) => (
            <SortableItem key={item.id} id={item.id}>
              {({ attributes, listeners }) => (
                <Card className="p-4">
                  <div className="flex items-start gap-3">
                    <span
                      {...attributes}
                      {...listeners}
                      aria-label={t('dragToReorder')}
                      className="mt-7 inline-flex size-8 shrink-0 cursor-grab items-center justify-center rounded-control text-ink-faint hover:bg-surface-sunken active:cursor-grabbing"
                    >
                      <GripIcon className="size-4" />
                    </span>

                    <div className="min-w-0 flex-1">
                      <ItemFields item={item} pages={pages} onEdit={editItem} />

                      {item.children.length > 0 ? (
                        <ul className="mt-4 flex flex-col gap-3 border-s-2 border-line ps-4">
                          {item.children.map((child) => (
                            <li key={child.id} className="flex flex-col items-start gap-1">
                              <div className="w-full">
                                <ItemFields item={child} pages={pages} onEdit={editItem} />
                              </div>
                              <Button variant="ghost" size="sm" onClick={() => removeItem(child.id)}>
                                <TrashIcon className="size-4" />
                                {t('removeItem')}
                              </Button>
                            </li>
                          ))}
                        </ul>
                      ) : null}

                      <div className="mt-3 flex flex-wrap gap-2">
                        {/* Two levels only — a third is a menu nobody can use
                            on a phone, and the API refuses it anyway. */}
                        <Button variant="ghost" size="sm" onClick={() => addItem(item.id)}>
                          <PlusIcon className="size-4" />
                          {t('addSubItem')}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => removeItem(item.id)}>
                          <TrashIcon className="size-4" />
                          {t('removeItem')}
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              )}
            </SortableItem>
          )}
        </SortableGrid>
      )}

      {stored === null ? null : (
        <SaveBar
          saving={saving}
          dirty={dirty}
          message={error ? <Alert>{error}</Alert> : undefined}
          onDiscard={dirty ? () => setDraft(null) : undefined}
          onSave={async () => {
            if (await save(items)) setDraft(null);
          }}
        />
      )}
    </Page>
  );
}

function ItemFields({
  item,
  pages,
  onEdit,
}: {
  item: NavigationItemDto;
  pages: PageChoice[];
  onEdit: (id: string, patch: Partial<NavigationItemDto>) => void;
}) {
  const locale = useUiStore((state) => state.locale);
  const t = useT();

  return (
    <div className="grid gap-3 md:grid-cols-4">
      <Field label={t('labelEnglish')} htmlFor={`${item.id}-en`}>
        <Input
          id={`${item.id}-en`}
          dir="ltr"
          value={item.label.en}
          onChange={(event) => onEdit(item.id, { label: { ...item.label, en: event.target.value } })}
        />
      </Field>

      <Field label={t('labelArabic')} htmlFor={`${item.id}-ar`}>
        <Input
          id={`${item.id}-ar`}
          dir="rtl"
          value={item.label.ar}
          onChange={(event) => onEdit(item.id, { label: { ...item.label, ar: event.target.value } })}
        />
      </Field>

      <Field label={t('linkTo')} htmlFor={`${item.id}-kind`}>
        <Select
          id={`${item.id}-kind`}
          value={item.kind}
          onChange={(event) =>
            // The target means something different per kind, so it is cleared
            // rather than reinterpreted.
            onEdit(item.id, { kind: event.target.value as NavigationItemDto['kind'], value: '' })
          }
        >
          {KINDS.map((kind) => (
            <option key={kind} value={kind}>
              {t(`menuKind${kind}` as MessageKey)}
            </option>
          ))}
        </Select>
      </Field>

      <Field label={t('target')} htmlFor={`${item.id}-value`}>
        {item.kind === 'products' ? (
          <Input id={`${item.id}-value`} value={t('menuKindproducts')} disabled />
        ) : item.kind === 'category' ? (
          <AsyncSelect
            id={`${item.id}-value`}
            value={item.value}
            emptyLabel={t('chooseCategory')}
            /* The category's own slug is stored — what the shop routes on — and
               named again by it when the menu opens. Making one from the name
               broke on any category whose slug was not its English name, and on
               every Arabic one. */
            fetchPage={categorySlugOptions.page}
            fetchOne={categorySlugOptions.one}
            onChange={(slug) => onEdit(item.id, { value: slug })}
          />
        ) : item.kind === 'page' ? (
          <Select
            id={`${item.id}-value`}
            value={item.value}
            onChange={(event) => onEdit(item.id, { value: event.target.value })}
          >
            <option value="">{t('choose')}</option>
            {pages.map((page) => (
              <option key={page.slug} value={page.slug}>
                {page.written ? page.label[locale] : t('menuPageNotWritten', { page: page.label[locale] })}
              </option>
            ))}
          </Select>
        ) : (
          <Input
            id={`${item.id}-value`}
            dir="ltr"
            placeholder="/products?sort=price_asc"
            value={item.value}
            onChange={(event) => onEdit(item.id, { value: event.target.value })}
          />
        )}
      </Field>
    </div>
  );
}
